import React, { useContext, useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import { UserContext } from '../contexts/UserContext';
import { MembershipContext } from '../contexts/MembershipContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useAdmin } from '../contexts/AdminContext';
import SubscriptionModal from './SubscriptionModal';
import NotificationDropdown from './NotificationDropdown';
import { isPedalNewsUiEnabled } from '../featureFlags';
import UserSearchSuggestions from './UserSearchSuggestions';
import { useOffersAwaitingResponse } from '../hooks/useOffersAwaitingResponse';
import { formatBadgeCount } from '../utils/notificationDisplay';

const NavBar = ({ onCreateMatch }) => {
  const { firestoreUser, completedTrades, username, isVerified, photoURL, loading } = useContext(UserContext);
  const { membershipTier, isProMember } = useContext(MembershipContext);
  const { tradeNotificationCount, matchNotificationCount } = useNotifications();
  const { isAdmin, isLoading: adminLoading, adminData } = useAdmin();
  
  // Debug logging for admin status
  React.useEffect(() => {
    console.log('🛡️ NavBar - Admin loading:', adminLoading);
    console.log('🛡️ NavBar - Is admin:', isAdmin);
    console.log('🛡️ NavBar - Admin data:', adminData);
  }, [isAdmin, adminLoading, adminData]);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isOverflowOpen, setIsOverflowOpen] = useState(false);
  const overflowRef = useRef(null);
  const navigate = useNavigate();
  const navRef = useRef(null);

  const updateNavbarHeight = useCallback(() => {
    if (navRef.current) {
      const style = getComputedStyle(navRef.current);
      const totalHeight = navRef.current.offsetHeight + parseFloat(style.marginBottom || 0);
      document.documentElement.style.setProperty('--navbar-height', `${totalHeight}px`);
    }
  }, []);

  useEffect(() => {
    updateNavbarHeight();
    const observer = new ResizeObserver(updateNavbarHeight);
    if (navRef.current) observer.observe(navRef.current);
    return () => observer.disconnect();
  }, [updateNavbarHeight]);

  // Close the desktop "+" overflow dropdown on outside click or Escape
  useEffect(() => {
    if (!isOverflowOpen) return;
    const handleClickOutside = (e) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target)) {
        setIsOverflowOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsOverflowOpen(false);
    };
    // Also close if the viewport grows past the "all-inline" threshold so the dropdown
    // doesn't get stranded open behind the now-hidden "+" wrapper. 1535px matches the
    // largest "+ disappears" breakpoint (news-on case); narrower widths never need this.
    const handleResize = () => {
      if (window.innerWidth >= 1535) setIsOverflowOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOverflowOpen]);

  const handleLogout = async () => {
    await signOut(auth);
    window.location.href = '/';
    window.location.reload();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    // Blur the active element (search input) to reset mobile zoom before navigating
    if (document.activeElement) {
      document.activeElement.blur();
    }
    
    // Small delay to allow blur to take effect before navigation
    setTimeout(() => {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }, 50);
  };

  const getTradeBadge = (count) => {
    const num = typeof count === 'number' ? count : parseInt(count, 10);
    if (isNaN(num)) return 0;
    return num; // Show exact number of completed trades, including 0
  };

  const badge = getTradeBadge(completedTrades);
  // Open offers waiting on this user (owner, 2026-11-02). Same number the
  // bottom tab bar and the Trading hub's Offers tile show; see the hook.
  const offersAwaitingResponse = useOffersAwaitingResponse();
  console.log('completedTrades from context:', completedTrades);

  const getMembershipBadge = () => {
    switch (membershipTier) {
      case 'premium_basic':
        return (
          <div className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            <span className="bg-blue-600 text-white px-1.5 py-0.5 rounded font-bold mr-1" style={{ fontSize: '11px' }}>9V</span>
            Member
          </div>
        );
      case 'premium_plus':
        return (
          <div className="flex items-center bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            <span className="bg-purple-600 text-white px-1.5 py-0.5 rounded font-bold mr-1" style={{ fontSize: '11px' }}>18V</span>
            Member
          </div>
        );
      default:
        return (
          <div className="flex items-center bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            <span className="bg-gray-500 text-white px-1.5 py-0.5 rounded font-bold mr-1" style={{ fontSize: '11px' }}>Free</span>
            Member
          </div>
        );
    }
  };

  const getMembershipProfileBadge = () => {
    switch (membershipTier) {
      case 'premium_basic':
        return (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              backgroundColor: '#2563eb',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px',
              width: '20px',
              height: '12px',
              fontSize: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.3)',
            }}
          >
            9V
          </div>
        );
      case 'premium_plus':
        return (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              backgroundColor: '#7c3aed',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px',
              width: '20px',
              height: '12px',
              fontSize: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.3)',
            }}
          >
            18V
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <nav ref={navRef} className='bg-gray-100 px-4 shadow-sm text-sm' style={{ position: 'relative', zIndex: 10, paddingTop: 'calc(14px + env(safe-area-inset-top))', paddingBottom: '6px' }}>
      <style dangerouslySetInnerHTML={{
        __html: `
          .mobile-break {
            display: none;
          }
          @media (max-width: 768px) {
            .mobile-break {
              display: block !important;
            }
          }
          /* Desktop layout */
          @media (min-width: 1025px) {
            .navbar-desktop-layout {
              display: flex !important;
              align-items: center !important;
              padding: 4px 2% !important;
              max-width: 1800px !important;
              margin-left: auto !important;
              margin-right: auto !important;
            }
            .navbar-mobile-logo-only {
              display: none !important;
            }
          }
          /* Tablet and mobile: hide desktop, show logo-only */
          @media (max-width: 1024px) {
            .navbar-desktop-layout {
              display: none !important;
            }
            .navbar-mobile-logo-only {
              display: block !important;
            }
          }
          @media (max-width: 1024px) {
            .navbar-logo {
              height: 5rem !important;
              margin-left: 10px !important;
            }
          }
          @media (max-width: 768px) {
            .navbar-logo {
              height: 5rem !important;
              margin-left: 10px !important;
            }
          }

          /* === Desktop nav-link overflow === */
          /* Defaults (smallest desktop, 1025px+ baseline): collapsibles hidden inline,
             present in dropdown, "+" wrapper visible. Each item un-collapses at its own
             min-width breakpoint. Two cascades because the "News ON/OFF" feature flag
             changes how much horizontal real-estate the row consumes. */
          .nav-links-row .nav-collapse-news,
          .nav-links-row .nav-collapse-match-opps,
          .nav-links-row .nav-collapse-want-list { display: none !important; }
          .nav-links-row .dropdown-item-news,
          .nav-links-row .dropdown-item-match-opps,
          .nav-links-row .dropdown-item-want-list { display: block !important; }
          .nav-links-row .nav-overflow-wrapper { display: inline-flex !important; }

          /* News ON: 3 collapse breakpoints (News falls out first as viewport grows) */
          @media (min-width: 1090px) {
            .news-on .nav-collapse-news { display: inline-block !important; }
            .news-on .dropdown-item-news { display: none !important; }
          }
          @media (min-width: 1330px) {
            .news-on .nav-collapse-match-opps { display: inline-block !important; }
            .news-on .dropdown-item-match-opps { display: none !important; }
          }
          @media (min-width: 1560px) {
            .news-on .nav-collapse-want-list { display: inline-block !important; }
            .news-on .dropdown-item-want-list { display: none !important; }
            .news-on .nav-overflow-wrapper { display: none !important; }
          }

          /* News OFF: 2 collapse breakpoints (News link itself never renders) */
          @media (min-width: 1180px) {
            .news-off .nav-collapse-match-opps { display: inline-block !important; }
            .news-off .dropdown-item-match-opps { display: none !important; }
          }
          @media (min-width: 1400px) {
            .news-off .nav-collapse-want-list { display: inline-block !important; }
            .news-off .dropdown-item-want-list { display: none !important; }
            .news-off .nav-overflow-wrapper { display: none !important; }
          }
        `
      }} />

      {/* === DESKTOP LAYOUT === */}
      <div className="navbar-desktop-layout" style={{ display: 'none' }}>

        {/* LEFT: Logo */}
        <Link to='/dashboard' style={{ flexShrink: 0, marginRight: '1.5rem', alignSelf: 'center' }}>
          <img src='/images/logo(white).svg' alt='Pedalxchange Logo' className="navbar-logo" style={{ height: '5rem', cursor: 'pointer', objectFit: 'contain' }} />
        </Link>

        {/* CENTER: Search on top, nav links on bottom */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }}>
          {/* Top: Search bar */}
          <div style={{ position: 'relative', maxWidth: '695px', width: '100%' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSearch(e);
                }
              }}
              placeholder="Search by make, model, effect, or user..."
              className="border border-gray-300 focus:border-blue-500 focus:outline-none shadow-sm"
              style={{ borderRadius: '25px', width: '100%', paddingRight: '40px', paddingLeft: '16px', paddingTop: '10px', paddingBottom: '10px', fontSize: '0.95rem', boxSizing: 'border-box' }}
            />
            <UserSearchSuggestions searchText={searchQuery} />
            <button
              type="button"
              onClick={handleSearch}
              style={{ position: 'absolute', right: '2px', top: '0', bottom: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', background: 'transparent', border: 'none', padding: '0', cursor: 'pointer', color: '#6b7280' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* Bottom: Nav links.
              Inline order at widest viewport: Dashboard · Browse · My Pedals · Trading · Add Pedal · News · Match Opportunities · Wantlist Matches · Admin
              As viewport narrows, items collapse RIGHT-to-LEFT into the "+" overflow:
                <1560/1400px → Wantlist Matches collapses
                <1330/1180px → Match Opportunities collapses
                <1090px (news-on only) → News collapses
              Add Pedal and Admin always remain inline. */}
          <div className={`nav-links-row ${isPedalNewsUiEnabled() ? 'news-on' : 'news-off'}`} style={{ display: 'flex', alignItems: 'center', gap: 'clamp(1rem, 2.8vw, 3.5rem)', flexWrap: 'nowrap', position: 'relative', top: '4px', left: '8px' }}>
            <Link to='/dashboard' style={{ color: '#5A9ECB', fontSize: '1.2rem', textDecoration: 'none', whiteSpace: 'nowrap' }}>Dashboard</Link>
            <Link to='/latest-pedals' state={{ fresh: true }} data-tip-anchor="browse" style={{ color: '#5A9ECB', fontSize: '1.2rem', textDecoration: 'none', whiteSpace: 'nowrap' }}>Browse</Link>
            <Link to='/my-pedals/pedalboard' style={{ color: '#5A9ECB', fontSize: '1.2rem', textDecoration: 'none', whiteSpace: 'nowrap' }}>My Pedals</Link>
            <Link to='/trading' style={{ color: '#5A9ECB', fontSize: '1.2rem', textDecoration: 'none', whiteSpace: 'nowrap', position: 'relative', display: 'inline-block' }}>
              Trading
              {offersAwaitingResponse > 0 && (
                // Same pill as the bell badge (NotificationDropdown): red, white
                // ring, bold count, "10+" cap. Sits on the word's top-right corner.
                <span
                  aria-label={`${offersAwaitingResponse} ${offersAwaitingResponse === 1 ? 'offer' : 'offers'} waiting for your response`}
                  style={{
                    // Overlaps the last letter's top-right the way the bell badge
                    // overlaps the bell (owner, 2026-11-02), rather than floating
                    // beside the word.
                    position: 'absolute', top: '-6px', right: '-14px',
                    height: '20px', minWidth: '20px', padding: '0 6px', boxSizing: 'border-box',
                    borderRadius: '10px', backgroundColor: '#dc2626', color: 'white',
                    border: '2px solid white', fontSize: '11px', fontWeight: 800, lineHeight: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'Nunito, sans-serif', pointerEvents: 'none', whiteSpace: 'nowrap',
                  }}
                >
                  {formatBadgeCount(offersAwaitingResponse)}
                </span>
              )}
            </Link>
            <Link to='/addpedal' style={{ color: '#C89F3C', fontSize: '1.2rem', textDecoration: 'none', fontWeight: '600', whiteSpace: 'nowrap' }}>Add Pedal</Link>

            {isPedalNewsUiEnabled() && (
              <Link className="nav-collapse-news" to='/news' style={{ color: '#5A9ECB', fontSize: '1.2rem', textDecoration: 'none', whiteSpace: 'nowrap' }}>News</Link>
            )}
            {/* Wantlist Matches & Match Opportunities are user-specific (require a logged-in account).
                When logged out, omit them entirely AND skip the "+" overflow — there's nothing to surface. */}
            {firestoreUser && (
              <>
                <Link className="nav-collapse-match-opps" to='/recommendations' style={{ color: '#5A9ECB', fontSize: '1.2rem', textDecoration: 'none', whiteSpace: 'nowrap' }}>Trade Opportunities</Link>
                <Link className="nav-collapse-want-list" to='/want-list-matches' style={{ color: '#5A9ECB', fontSize: '1.2rem', textDecoration: 'none', whiteSpace: 'nowrap' }}>Saved Pedals</Link>
              </>
            )}

            {/* "+" overflow button — only when logged in (logged-out has nothing to overflow). */}
            {firestoreUser && (
            <div className="nav-overflow-wrapper" ref={overflowRef} style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setIsOverflowOpen((v) => !v)}
                aria-label="More navigation"
                aria-expanded={isOverflowOpen}
                aria-haspopup="menu"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  background: 'none',
                  border: 'none',
                  padding: '0',
                  cursor: 'pointer',
                  fontFamily: 'inherit'
                }}
              >
                {/* Gold pill containing "+" and a white down-caret, both inside */}
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    height: '32px',
                    padding: '0 12px',
                    borderRadius: '16px',
                    backgroundColor: '#C89F3C',
                    color: '#ffffff',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.18)'
                  }}
                >
                  <span style={{ fontWeight: 700, fontSize: '1.25rem', lineHeight: 1 }}>+</span>
                  <span
                    style={{
                      fontSize: '1.15rem',
                      lineHeight: 1,
                      transform: isOverflowOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.15s'
                    }}
                  >
                    ▾
                  </span>
                </span>
              </button>

              {isOverflowOpen && (
                <div
                  role="menu"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    left: '0',
                    minWidth: '220px',
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                    padding: '6px 0',
                    zIndex: 1000
                  }}
                >
                  {isPedalNewsUiEnabled() && (
                    <Link
                      className="dropdown-item-news"
                      to='/news'
                      role="menuitem"
                      onClick={() => setIsOverflowOpen(false)}
                      style={{ padding: '10px 16px', fontSize: '1rem', color: '#5A9ECB', textDecoration: 'none', whiteSpace: 'nowrap' }}
                    >
                      News
                    </Link>
                  )}
                  <Link
                    className="dropdown-item-match-opps"
                    to='/recommendations'
                    role="menuitem"
                    onClick={() => setIsOverflowOpen(false)}
                    style={{ padding: '10px 16px', fontSize: '1rem', color: '#5A9ECB', textDecoration: 'none', whiteSpace: 'nowrap' }}
                  >
                    Trade Opportunities
                  </Link>
                  <Link
                    className="dropdown-item-want-list"
                    to='/want-list-matches'
                    role="menuitem"
                    onClick={() => setIsOverflowOpen(false)}
                    style={{ padding: '10px 16px', fontSize: '1rem', color: '#5A9ECB', textDecoration: 'none', whiteSpace: 'nowrap' }}
                  >
                    Saved Pedals
                  </Link>
                </div>
              )}
            </div>
            )}

            {isAdmin && (
              <Link to='/admin' style={{ color: '#dc2626', fontSize: '1.2rem', textDecoration: 'none', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Admin</Link>
            )}
          </div>
        </div>

        {/* RIGHT: Bell on the left, avatar+logout column on the right */}
        {!loading && (
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-end', gap: '6px', marginLeft: '1.5rem', alignSelf: 'flex-end', paddingBottom: '2px' }}>
            {firestoreUser ? (
              <>
                {/* Bell to the left, bottom-aligned with Logout */}
                <div style={{ position: 'relative', top: '4px' }}>
                  <NotificationDropdown />
                </div>

                {/* Avatar + Logout stacked */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                  <Link to='/profile' style={{ position: 'relative', display: 'block' }}>
                    <img
                      src={photoURL || '/images/default-avatar.png'}
                      alt='User Avatar'
                      data-tip-anchor="profile"
                      style={{ width: '52px', height: '52px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #ccc', cursor: 'pointer' }}
                    />
                    {getMembershipProfileBadge()}
                    {badge !== null && (
                      <div style={{ position: 'absolute', bottom: '-1px', right: '-1px', backgroundColor: 'gold', color: '#000', fontWeight: 'bold', borderRadius: '50%', width: '18px', height: '18px', fontSize: '0.6rem', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #fff', boxShadow: '0 0 2px rgba(0,0,0,0.3)' }}>
                        {badge}
                      </div>
                    )}
                  </Link>
                  <button
                    onClick={handleLogout}
                    style={{ fontSize: '13.5px', color: '#5A9ECB', fontFamily: 'Nunito, sans-serif', background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '52px', height: '52px' }}>
                  <img src='/images/default-avatar.png' alt='Guest Avatar' style={{ width: '52px', height: '52px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #ccc' }} />
                </div>
                <Link to='/login' style={{ fontSize: '13.5px', color: '#5A9ECB', textDecoration: 'none', position: 'relative', top: '3px' }}>Log In</Link>
              </div>
            )}
          </div>
        )}
      </div>

      {/* === TABLET/MOBILE: Logo + profile icon + bell (nav is in bottom tab bar) === */}
      <div className="navbar-mobile-logo-only" style={{ display: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 16px' }}>
          <Link to='/dashboard'>
            <img src='/images/logo(white).svg' alt='Pedalxchange Logo' className="navbar-logo" style={{ height: '5rem', cursor: 'pointer', objectFit: 'contain' }} />
          </Link>

          {!loading && (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', marginRight: '11px' }}>
              {firestoreUser ? (
                <>
                  <div style={{ position: 'relative', top: '4px' }}>
                    <NotificationDropdown />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                    <Link to='/profile' style={{ position: 'relative', display: 'block' }}>
                      <img
                        src={photoURL || '/images/default-avatar.png'}
                        alt='User Avatar'
                        data-tip-anchor="profile"
                        style={{ width: '46px', height: '46px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #ccc', cursor: 'pointer' }}
                      />
                      {getMembershipProfileBadge()}
                      {badge !== null && (
                        <div style={{ position: 'absolute', bottom: '-1px', right: '-1px', backgroundColor: 'gold', color: '#000', fontWeight: 'bold', borderRadius: '50%', width: '16px', height: '16px', fontSize: '0.55rem', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #fff', boxShadow: '0 0 2px rgba(0,0,0,0.3)' }}>
                          {badge}
                        </div>
                      )}
                    </Link>
                    <button
                      onClick={handleLogout}
                      style={{ fontSize: '13.5px', color: '#5A9ECB', fontFamily: 'Nunito, sans-serif', background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', position: 'relative', top: '3px' }}
                    >
                      Logout
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '46px', height: '46px' }}>
                    <img src='/images/default-avatar.png' alt='Guest Avatar' style={{ width: '46px', height: '46px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #ccc' }} />
                  </div>
                  <Link to='/login' style={{ fontSize: '13.5px', color: '#5A9ECB', textDecoration: 'none', position: 'relative', top: '6px' }}>Log In</Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Gray line beneath navbar - Always Visible */}
      <div style={{ 
        height: '2px', 
        backgroundColor: '#4A5759', 
        margin: '0 10%', 
        marginTop: '0.5rem',
        position: 'relative',
        zIndex: 1
      }}></div>
      
      {/* Mobile Menu Dropdown - HIDDEN (replaced by bottom tab bar) */}
      {false && isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 z-[9999]"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div 
            className="bg-white shadow-lg"
            onClick={(e) => e.stopPropagation()}
            style={{
              animation: 'slideIn 0.3s ease-out',
              transform: 'translateX(0)',
              zIndex: 10000,
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              border: '1px solid #e5e7eb',
              width: '262px',
              height: 'calc(100vh - 292px)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <style jsx>{`
              @keyframes slideIn {
                from { transform: translateX(-100%); }
                to { transform: translateX(0); }
              }
            `}</style>
            
            {/* Mobile Menu Header */}
            <div style={{ 
              padding: '16px 20px', 
              borderBottom: '1px solid #e5e7eb', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center' 
            }}>
              <span style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#1f2937', 
                margin: 0 
              }}>
                Menu
              </span>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer', 
                  fontSize: '12px',
                  color: '#9ca3af',
                  marginLeft: '20px'
                }}
              >
                ✕
              </button>
            </div>
            
            {/* Mobile Navigation Links */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <Link 
                to='/dashboard' 
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ 
                  display: 'block',
                  padding: '16px 20px',
                  borderBottom: '1px solid #f3f4f6',
                  fontSize: '16px', 
                  color: '#5A9ECB', 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                Dashboard
              </Link>
              <Link 
                to='/latest-pedals'
                state={{ fresh: true }}
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ 
                  display: 'block',
                  padding: '16px 20px',
                  borderBottom: '1px solid #f3f4f6',
                  fontSize: '16px', 
                  color: '#5A9ECB', 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                Browse
              </Link>
              <Link 
                to='/my-pedals/pedalboard' 
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ 
                  display: 'block',
                  padding: '16px 20px',
                  borderBottom: '1px solid #f3f4f6',
                  fontSize: '16px', 
                  color: '#5A9ECB', 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                Pedalboard
              </Link>
              <Link 
                to='/wantlist' 
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ 
                  display: 'block',
                  padding: '16px 20px',
                  borderBottom: '1px solid #f3f4f6',
                  fontSize: '16px', 
                  color: '#5A9ECB', 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                Wantlist
              </Link>
              <Link 
                to='/matches' 
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ 
                  display: 'block',
                  padding: '16px 20px',
                  borderBottom: '1px solid #f3f4f6',
                  fontSize: '16px', 
                  color: '#5A9ECB', 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                Matches
              </Link>
              <Link 
                to='/trade-center' 
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ 
                  display: 'block',
                  padding: '16px 20px',
                  borderBottom: '1px solid #f3f4f6',
                  fontSize: '16px', 
                  color: '#5A9ECB', 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                Trade Center
              </Link>
              {isAdmin && (
                <Link 
                  to='/admin' 
                  onClick={() => setIsMobileMenuOpen(false)}
                  style={{ 
                    display: 'block',
                    padding: '12px 20px',
                    borderBottom: '1px solid #f3f4f6',
                    fontSize: '16px', 
                    color: '#dc2626', 
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#fef2f2'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  Admin
                </Link>
              )}
            </div>
            
            {/* Mobile Search Bar - Fixed at bottom */}
            <div className="mobile-search-container">
              <div className="relative mobile-search-input-container">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSearch(e);
                      setIsMobileMenuOpen(false);
                    }
                  }}
                  placeholder="Search by make, model, effect, or user..."
                  className="w-full px-4 py-2 text-sm border border-gray-300 focus:border-blue-500 focus:outline-none rounded-lg pr-10 mobile-search-input"
                />
                <UserSearchSuggestions searchText={searchQuery} onSelect={() => setIsMobileMenuOpen(false)} />
                <button
                  type="button"
                  onClick={() => {
                    handleSearch({ preventDefault: () => {} });
                    setIsMobileMenuOpen(false);
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 mobile-search-button"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Subscription Modal */}
      <SubscriptionModal 
        isOpen={isSubscriptionModalOpen} 
        onClose={() => setIsSubscriptionModalOpen(false)} 
      />
      
      <style jsx>{`
        .mobile-search-container {
          padding: 20px 24px 20px 24px !important;
          border-top: 1px solid #e5e7eb !important;
          margin-bottom: 0 !important;
        }
        
        .mobile-search-input-container {
          margin-bottom: 0 !important;
          padding-bottom: 0 !important;
          margin: 0 !important;
        }
        
        .mobile-search-input {
          border-radius: 25px !important;
          margin-bottom: 0 !important;
          margin: 0 !important;
        }
        
        .mobile-search-button {
          margin-bottom: 0 !important;
          margin: 0 !important;
        }
      `}</style>
    </nav>
  );
};

export default NavBar;
